import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FieldHelp } from '@/components/forms/FieldHelp';
import { api, ApiError } from '@/lib/api-client';
import { toast } from '@/components/ui/toast';
import { fromMinor } from '@/lib/money';
import { useAgentCurrencyMap, type AgentCurrencyResolver } from '@/hooks/useAgentCurrency';

interface BulkApproveResult {
  approved: number;
  /**
   * Rows the caller's ceiling refused. NOT a failure: each one moved up a rung
   * carrying this batch's remark, and is still going to be paid by somebody.
   * Reported beside `approved` rather than folded into `skipped`, so one large
   * payout going upstairs does not read as twenty payouts going wrong.
   */
  escalated: number;
  skipped: number;
  failures: { id: string; code: string }[];
  approvedIds: string[];
  escalatedIds: string[];
}

export interface PendingWithdrawRow {
  id: string;
  amount: number;
  status: string;
  /** Which agent's money this is — its currency comes from there (H8). */
  agentId?: string | null;
}

/**
 * One total per currency, plus how many rows we could not denominate.
 *
 * Exported so the component and its tests share one implementation — a
 * re-implementation in the test file is a test that passes whatever the
 * component does.
 */
export function totalsByCurrency(
  rows: PendingWithdrawRow[],
  currencyOf: AgentCurrencyResolver,
): { totals: Array<[string, number]>; unresolved: number } {
  const acc = new Map<string, number>();
  let unresolved = 0;
  for (const r of rows) {
    const code = currencyOf(r.agentId);
    if (!code) {
      unresolved++;
      continue;
    }
    acc.set(code, (acc.get(code) ?? 0) + r.amount);
  }
  return {
    // Stable order, so the dialog does not reshuffle between renders.
    totals: [...acc.entries()].sort((a, b) => a[0].localeCompare(b[0])),
    unresolved,
  };
}

/**
 * Approve the pending withdrawals the operator is LOOKING AT.
 *
 * This used to post an empty body: the server built its own worklist at
 * execution time, so the operator's filters and page were ignored — filter to
 * one member to handle a ticket, click, and every pending payout in the agent
 * scope was authorised, including rows that arrived while the confirmation
 * dialog was open. The confirmation itself was two static strings with no count
 * and no total, so there was no way to notice (BO-03).
 *
 * Now the visible pending rows are sent explicitly, with the total they sum to;
 * the server refuses the whole batch if either has moved.
 */
export function BulkApproveWithdrawsButton({
  invalidateKey,
  rows,
}: {
  invalidateKey: string;
  /** The rows currently rendered, in the operator's current filter and page. */
  rows: PendingWithdrawRow[];
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  // One reason for the whole batch, and the server refuses a blank one for every
  // caller (FIN-332) — this only saves the operator the round trip. Cleared on
  // each open: a remark left standing would be attached to the NEXT batch, which
  // is a different set of payouts.
  const [reason, setReason] = React.useState('');
  const remarkMissing = reason.trim() === '';
  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const currencyOf = useAgentCurrencyMap();

  const pending = React.useMemo(() => rows.filter((r) => r.status === 'pending'), [rows]);
  const totalMinor = React.useMemo(
    () => pending.reduce((sum, r) => sum + r.amount, 0),
    [pending],
  );

  // One line per currency. An L6 operator's scope can span agents denominated
  // differently, and summing their minor units under one glyph produces a
  // number that means nothing — on the screen whose entire job is to let a human
  // authorise an amount (H8). `expectedTotalMinor` on the wire stays the plain
  // sum, because the server compares it against the same integers.
  //
  // A row whose currency has not resolved is counted separately, never folded
  // into a THB line: the org list is `undefined` while in flight and after any
  // failure, so that substitution turned the whole batch into "THB" on a plain
  // network race (N6).
  const { totals, unresolved } = React.useMemo(
    () => totalsByCurrency(pending, currencyOf),
    [pending, currencyOf],
  );

  const mutation = useMutation({
    mutationFn: () =>
      api.post<BulkApproveResult>('/api/v1/withdraw-decisions/bulk-approve', {
        ids: pending.map((r) => r.id),
        reason,
        expectedTotalMinor: totalMinor,
      }),
    onSuccess: (res) => {
      // Both counts, always — an operator who is told only "2 approved" of three
      // rows has to work out for themselves where the third went.
      toast.success(
        t('finance.withdrawAllResult', {
          approved: res.approved,
          escalated: res.escalated,
          skipped: res.skipped,
        }),
      );
      qc.invalidateQueries({ queryKey: [invalidateKey] });
      setOpen(false);
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.displayMessage);
      else toast.error('Action failed');
    },
  });

  return (
    <>
      <Button variant="default" onClick={() => setOpen(true)} disabled={pending.length === 0}>
        {t('finance.withdrawAll')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('finance.withdrawAllConfirmTitle')}</DialogTitle>
            {/* The operator cannot authorise a payout batch whose size they
                were never told. */}
            <DialogDescription>
              {unresolved > 0
                ? t('finance.currencyUnknown')
                : t('finance.withdrawAllConfirmBody', {
                    count: pending.length,
                    total: totals
                      .map(([code, minor]) => `${fromMinor(minor, code)} ${code}`)
                      .join(' + '),
                    currency: totals.map(([code]) => code).join(', '),
                  })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="bulkApproveReason" className="flex items-center gap-1.5">
              {t('finance.remark')}
              <FieldHelp text={t('finance.bulkRemarkHelp')} />
            </Label>
            <Textarea
              id="bulkApproveReason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              aria-required
            />
            {remarkMissing && (
              <p role="alert" className="text-sm text-destructive">
                {t('finance.remarkRequired')}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="default"
              onClick={() => mutation.mutate()}
              disabled={
                mutation.isPending || pending.length === 0 || unresolved > 0 || remarkMissing
              }
            >
              {t('finance.withdrawAll')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
